import { validators } from '@approvalmax/ui';
import {
    BusinessSegment,
    NumberOfClients,
    NumberOfEntities,
    PartnerSegment,
    PartnerStatus,
    ProductInterest,
    TypeOfBusiness,
    UseCase,
} from 'modules/data/domain';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import { fields } from '../../constants';
import { messages } from './ProfileSetupForm.messages';

export const getSchema = (params: {
    showPasswordField?: boolean;
    showPhoneField?: boolean;
    showConsent?: boolean;
    showQuestions?: boolean;
}) => {
    const { showPhoneField, showPasswordField, showConsent, showQuestions } = params;

    return z
        .object({
            [fields.fieldNameFirstName]: z.string().trim().min(1, { message: messages.errorFirstNameRequired }),
            [fields.fieldNameLastName]: z.string().trim().min(1, { message: messages.errorLastNameRequired }),
            [fields.fieldNamePassword]: showPasswordField
                ? z
                      .string()
                      .trim()
                      .min(1, { message: messages.errorPasswordRequired })
                      .refine((value) => !validators.password(value), { message: messages.errorPasswordInvalid })
                : z.any(),
            [fields.fieldNamePhone]: showPhoneField
                ? z
                      .string()
                      .trim()
                      .min(1, { message: messages.errorPhoneRequired })
                      .refine((value) => isPossiblePhoneNumber(value), { message: messages.errorPhoneInvalid })
                : z.any(),
            [fields.fieldNameTypeOfBusiness]: z
                .nativeEnum(TypeOfBusiness, { message: messages.errorTypeOfBusinessRequired })
                .nullable(),
            [fields.fieldNamePartnerStatus]: z
                .nativeEnum(PartnerStatus, { message: messages.errorPartnerStatus })
                .nullable(),
            [fields.fieldNameProductInterest]: z
                .nativeEnum(ProductInterest, { message: messages.errorProductInterest })
                .nullable(),
            [fields.fieldNameNumberOfEntities]: z
                .nativeEnum(NumberOfEntities, { message: messages.errorNumberOfEntities })
                .nullable(),
            [fields.fieldNameNumberOfClients]: z
                .nativeEnum(NumberOfClients, { message: messages.errorNumberOfClients })
                .nullable(),
            [fields.fieldNameUseCase]: z.array(z.nativeEnum(UseCase, { message: messages.errorUseCase })).nullable(),
            [fields.fieldNamePartnerSegment]: z
                .nativeEnum(PartnerSegment, { message: messages.errorPartnerSegment })
                .nullable(),
            [fields.fieldNameBusinessSegment]: z
                .nativeEnum(BusinessSegment, { message: messages.errorBusinessSegment })
                .nullable(),
            [fields.fieldNameConsentMarketingAccepted]: showConsent ? z.boolean().optional() : z.any(),
        })
        .superRefine((data, ctx) => {
            if (showQuestions && !data[fields.fieldNameTypeOfBusiness]) {
                ctx.addIssue({
                    path: [fields.fieldNameTypeOfBusiness],
                    message: messages.errorTypeOfBusinessRequired,
                    code: 'custom',
                });
            }

            if (data[fields.fieldNameTypeOfBusiness] === TypeOfBusiness.Partner) {
                if (!data[fields.fieldNamePartnerStatus]) {
                    ctx.addIssue({
                        path: [fields.fieldNamePartnerStatus],
                        message: messages.errorPartnerStatus,
                        code: 'custom',
                    });
                }

                if (!data[fields.fieldNamePartnerSegment]) {
                    ctx.addIssue({
                        path: [fields.fieldNamePartnerSegment],
                        message: messages.errorPartnerSegment,
                        code: 'custom',
                    });
                }

                if (!data[fields.fieldNameNumberOfClients]) {
                    ctx.addIssue({
                        path: [fields.fieldNameNumberOfClients],
                        message: messages.errorNumberOfClients,
                        code: 'custom',
                    });
                }
            }

            if (data[fields.fieldNameTypeOfBusiness] === TypeOfBusiness.Business) {
                if (!data[fields.fieldNameProductInterest]) {
                    ctx.addIssue({
                        path: [fields.fieldNameProductInterest],
                        message: messages.errorProductInterest,
                        code: 'custom',
                    });
                }

                if (!data[fields.fieldNameNumberOfEntities]) {
                    ctx.addIssue({
                        path: [fields.fieldNameNumberOfEntities],
                        message: messages.errorNumberOfEntities,
                        code: 'custom',
                    });
                }

                if (!data[fields.fieldNameBusinessSegment]) {
                    ctx.addIssue({
                        path: [fields.fieldNameBusinessSegment],
                        message: messages.errorBusinessSegment,
                        code: 'custom',
                    });
                }
            }

            if (
                data[fields.fieldNameTypeOfBusiness] === TypeOfBusiness.Partner ||
                data[fields.fieldNameTypeOfBusiness] === TypeOfBusiness.Business
            ) {
                if (!data[fields.fieldNameUseCase] || data[fields.fieldNameUseCase].length === 0) {
                    ctx.addIssue({
                        path: [fields.fieldNameUseCase],
                        message: messages.errorUseCase,
                        code: 'custom',
                    });
                }
            }
        });
};
